import React from 'react';
import { useCart } from '../context/CartContext';
import { X, ShoppingBag, ArrowRight, Trash2, XCircle } from 'lucide-react';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

// Sub-components
import CartItem from './Cart/CartItem';
import CheckoutAddressStep from './Cart/CheckoutAddressStep';
import CheckoutShippingStep from './Cart/CheckoutShippingStep';
import CartDrawerSummary from './Cart/CartDrawerSummary';

interface CartDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export interface AddressData {
    firstName: string;
    lastName: string;
    address: string;
    zip: string;
    city: string;
    region?: string;
    country: string;
    lat?: number;
    lon?: number;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
    const { items, removeFromCart, updateQuantity, totalAmount, clearCart, couponCode } = useCart();
    const { isAuthenticated, user, setUser, openAuthModal } = useAuth();
    const { t } = useTranslation(['marketplace', 'common']);

    const [isAddressModalOpen, setIsAddressModalOpen] = React.useState(false);
    const [isDifferentAddress, setIsDifferentAddress] = React.useState(false);
    const [email, setEmail] = React.useState('');
    const [addressData, setAddressData] = React.useState<AddressData>({
        firstName: '',
        lastName: '',
        address: '',
        zip: '',
        city: '',
        region: '',
        country: t('marketplace:checkout.default_country'),
        lat: undefined,
        lon: undefined
    });
    const [selectedShippingId, setSelectedShippingId] = React.useState<string | null>(null);
    const [availableRates, setAvailableRates] = React.useState<any[]>([]);
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [commissionPct, setCommissionPct] = React.useState(10);
    const [checkoutStep, setCheckoutStep] = React.useState<1 | 2>(1);
    const [isProcessing, setIsProcessing] = React.useState(false);

    const [defaultMinFee, setDefaultMinFee] = React.useState(0);
    const [exchangeRates, setExchangeRates] = React.useState<Record<string, number>>({});

    React.useEffect(() => {
        api.system.getPublicSettings().then(settings => {
            if (settings) {
                if (settings.default_shop_commission_percentage) {
                    setCommissionPct(parseFloat(settings.default_shop_commission_percentage));
                }
                if (settings.default_shop_min_fee) {
                    setDefaultMinFee(parseFloat(settings.default_shop_min_fee));
                }
                if (settings.currency_exchange_rates) {
                    try {
                        const cache = JSON.parse(settings.currency_exchange_rates);
                        setExchangeRates(cache.rates || {});
                    } catch (e) {
                        console.error("Failed to parse exchange rates cache", e);
                    }
                }
            }
        });
    }, []);

    // Sync Address Data when User logs in during checkout
    React.useEffect(() => {
        if (isAuthenticated && user && isAddressModalOpen && !isDifferentAddress) {
            const profileZip = user?.zip_code || '';
            let extractedZip = profileZip;
            if (!extractedZip && user?.departure_address) {
                const zipMatch = user.departure_address.match(/\b\d{4,5}\b/);
                if (zipMatch) extractedZip = zipMatch[0];
            }

            setAddressData({
                firstName: user?.first_name || '',
                lastName: user?.last_name || '',
                address: user?.address || user?.departure_address || '',
                zip: extractedZip,
                city: user?.departure_city || user?.city || '',
                region: user?.region || '',
                country: user?.country || t('marketplace:checkout.default_country'),
                lat: user?.departure_lat || undefined,
                lon: user?.departure_lon || undefined
            });
        }
    }, [isAuthenticated, user, isAddressModalOpen, isDifferentAddress]);

    const validateFields = () => {
        const newErrors: Record<string, string> = {};
        if (!addressData.firstName.trim()) newErrors.firstName = t('checkout.error_first_name_required' as any);
        if (!addressData.lastName.trim()) newErrors.lastName = t('checkout.error_last_name_required' as any);
        if (!isAuthenticated && !email.trim()) newErrors.email = t('checkout.error_email_required' as any);
        if (!isAuthenticated && email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = t('checkout.error_email_invalid' as any);
        if (!addressData.address.trim()) newErrors.address = t('checkout.error_address_required' as any);
        if (!addressData.zip.trim()) newErrors.zip = t('checkout.error_zip_required' as any);
        if (!addressData.city.trim()) newErrors.city = t('checkout.error_city_required' as any);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const startCheckout = () => {
        console.log("[CartDrawer] Starting checkout flow. Item count:", items.length);
        setAddressData({
            firstName: user?.first_name || '',
            lastName: user?.last_name || '',
            address: user?.address || '',
            zip: user?.zip_code || '',
            city: user?.city || '',
            region: user?.region || '',
            country: user?.country || t('marketplace:checkout.default_country'),
            lat: undefined,
            lon: undefined
        });

        setErrors({});
        setCheckoutStep(1);

        if (items.length > 0) {
            const isPod = items.some(i => i.shopId === 'PADEL_PARTNER_POD');

            // For POD, we wait for Step 1 validation. For Regular Shops, we fetch static content now.
            if (!isPod) {
                const firstShopId = items[0].shopId || (items[0] as any).shop_id;
                api.marketplace.getShop(firstShopId).then(data => {
                    let ratesArray = [];
                    if (data?.shipping_rates) {
                        try {
                            const rates = typeof data.shipping_rates === 'string'
                                ? JSON.parse(data.shipping_rates)
                                : data.shipping_rates;
                            ratesArray = Array.isArray(rates) ? rates : [];
                        } catch (err) {
                            console.error("[Checkout] Failed to parse shipping rates:", err);
                        }
                    }
                    if (data?.allows_pickup) {
                        ratesArray.push({
                            id: 'pickup',
                            name: t('marketplace:cart.shipping.pickup_name'),
                            price: 0,
                            estimated_days: t('marketplace:cart.shipping.pickup_estimate'),
                            is_pickup: true
                        });
                    }
                    setAvailableRates(ratesArray);
                    if (ratesArray.length > 0) setSelectedShippingId(ratesArray[0].id);
                }).catch(err => {
                    console.error("[Checkout] Failed to fetch shop data:", err);
                    setAvailableRates([]);
                });
            } else {
                setAvailableRates([]); // Clear rates until calculated
            }
        }
        setIsAddressModalOpen(true);
    };

    const handleDifferentAddressToggle = (checked: boolean) => {
        setIsDifferentAddress(checked);
        if (checked) {
            setAddressData({
                firstName: '',
                lastName: '',
                address: '',
                zip: '',
                city: '',
                region: '',
                country: t('marketplace:checkout.default_country'),
            });
        }
    };

    const handleSaveToProfile = async () => {
        if (!user || !isAuthenticated) return;
        setIsProcessing(true);
        try {
            // Fix concatenation: only add zip/city if not already at the end of addressData.address
            let departure_address = addressData.address.trim();
            const suffix = `${addressData.zip} ${addressData.city}`.trim();

            if (suffix && !departure_address.includes(addressData.zip) && !departure_address.includes(addressData.city)) {
                departure_address = `${departure_address}, ${suffix}`.trim();
            }

            const updatedUser = await api.users.update(user.id, {
                first_name: addressData.firstName,
                last_name: addressData.lastName,
                address: addressData.address,
                zip_code: addressData.zip,
                city: addressData.city,
                country: addressData.country,
                departure_address: departure_address,
                departure_city: addressData.city,
                departure_lat: addressData.lat,
                departure_lon: addressData.lon
            });
            if (updatedUser) {
                setUser(updatedUser);
                toast.success(t('checkout.address_saved_success'));
            }
        } catch (error) {
            console.error("Failed to update profile:", error);
            toast.error(t('common:errors.update_failed'));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCheckout = async () => {
        console.log("[CartDrawer] handleCheckout called. Step:", checkoutStep);
        if (!validateFields()) return;

        // Step 1 -> Step 2 (Calculations)
        if (checkoutStep === 1) {
            const isPod = items.some(i => i.shopId === 'PADEL_PARTNER_POD');

            if (isPod) {
                setIsProcessing(true);
                toast.loading(t('marketplace:cart.calculating_shipping'));

                try {
                    const countryCode = addressData.country.toUpperCase() === 'BELGIQUE' ? 'BE' : addressData.country.toUpperCase() === 'FRANCE' ? 'FR' : 'BE'; // Simple fallback

                    const rates = await api.pod.calculateShipping({
                        address1: addressData.address,
                        city: addressData.city,
                        country_code: countryCode,
                        zip: addressData.zip,
                        name: `${addressData.firstName} ${addressData.lastName}`,
                        email: email || user?.email,
                        phone: user?.phone
                    }, items.map(i => ({
                        variant_id: i.id,
                        quantity: i.quantity,
                        value: (i.price / 100).toFixed(2)
                    })));

                    toast.dismiss();
                    if (rates && Array.isArray(rates)) {
                        const podRates = rates.map((r: any) => ({
                            id: r.id,
                            //Remove duration from name (e.g. "Printful Flat Rate (3-5 business days)" -> "Printful Flat Rate")
                            name: r.name.replace(/\s*\(.*?\)\s*/g, '').trim(),
                            price: parseFloat(r.rate) * 100,
                            estimated_days: (r.min_delivery_days && r.max_delivery_days)
                                ? t('cart.shipping_days_format', { min: r.min_delivery_days, max: r.max_delivery_days })
                                : (r.rate < 5 ? '3-7 jours' : '2-4 jours')
                        }));
                        setAvailableRates(podRates);
                        if (podRates.length > 0) setSelectedShippingId(podRates[0].id);

                        setCheckoutStep(2); // Success -> Next Step
                    } else {
                        setAvailableRates([]);
                        toast.error(t('cart.no_shipping_rates'));
                    }
                } catch (err: any) {
                    toast.dismiss();
                    console.error("[Checkout] POD Shipping Error:", err);

                    if (err.message === 'Address Verification Failed') {
                        toast.error(t('cart.address_verification_failed'), { duration: 5000 });
                        // Do NOT advance step
                    } else {
                        toast.error(t('cart.shipping_calc_error'));
                    }
                } finally {
                    setIsProcessing(false);
                }
                return;
            } else {
                setCheckoutStep(2);
                return;
            }
        }

        if (isProcessing) return;

        try {
            setIsProcessing(true);
            toast.loading(t('marketplace:cart.preparing_payment'));

            const enrichedItems = items.map(item => ({
                ...item,
                shipping_name: `${addressData.firstName} ${addressData.lastName}`.trim(),
                shipping_address: addressData.address,
                shipping_city: addressData.city,
                shipping_zip: addressData.zip,
                shipping_country: addressData.country,
                is_different_address: isDifferentAddress,
                customer_email: !isAuthenticated ? email : undefined
            }));

            const isMobile = window.location.pathname.startsWith('/app');
            const origin = window.location.origin;
            const successUrl = isMobile
                ? `${origin}/app/marketplace/success?session_id={CHECKOUT_SESSION_ID}`
                : `${origin}/marketplace/success?session_id={CHECKOUT_SESSION_ID}`;

            const cancelUrl = isMobile
                ? `${origin}/app/marketplace?canceled=true`
                : `${origin}/marketplace?canceled=true`;

            // Find selected rate to pass dynamic cost (for POD)
            const selectedRate = availableRates.find(r => r.id === selectedShippingId);

            const { url } = await api.marketplace.createCheckoutSession(
                enrichedItems,
                selectedShippingId || undefined,
                addressData.country,
                successUrl,
                cancelUrl,
                couponCode || undefined,
                selectedRate ? selectedRate.price : undefined,
                selectedRate ? selectedRate.name : undefined
            );
            if (url) window.location.href = url;
        } catch (error: any) {
            toast.dismiss();
            toast.error(error.message || t('cart.payment_failed'));
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] overflow-hidden">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
                <div className="p-6 pt-[calc(1.5rem+env(safe-area-inset-top))] border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ShoppingBag className="w-6 h-6 text-purple-600" />
                        <h2 className="text-xl font-bold text-gray-900">{t('cart.title')}</h2>
                        {items.length > 0 && (
                            <button
                                onClick={() => {
                                    if (window.confirm(t('cart.clear_confirm'))) {
                                        clearCart();
                                    }
                                }}
                                className="ml-2 p-2 hover:bg-red-50 text-red-500 rounded-full transition-colors group"
                                title={t('cart.clear')}
                            >
                                <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            </button>
                        )}
                    </div>
                    <button onClick={onClose} className="absolute top-[calc(env(safe-area-inset-top)+0.75rem)] right-3 sm:top-6 sm:right-6 p-2 sm:p-3 bg-white/90 backdrop-blur rounded-full shadow-lg z-[70] hover:bg-white transition-all active:scale-95 border border-slate-100">
                        <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center">
                            <ShoppingBag className="w-16 h-16 text-gray-200 mb-4" />
                            <p className="text-gray-500 font-medium">{t('marketplace:cart.empty_title')}</p>
                            <button onClick={onClose} className="mt-4 text-purple-600 font-bold hover:underline">
                                {t('marketplace:cart.continue_shopping')}
                            </button>
                        </div>
                    ) : (
                        items.map((item) => (
                            <CartItem
                                key={item.id}
                                item={{ ...item, badge: item.appliedCoupon || undefined, discountPercent: item.discountPercent } as any}
                                onUpdateQuantity={updateQuantity}
                                onRemove={removeFromCart}
                            />
                        ))
                    )}
                </div>

                {items.length > 0 && (
                    <CartDrawerSummary
                        totalAmount={totalAmount}
                        commissionPct={commissionPct}
                        minFee={defaultMinFee}
                        currency={items[0]?.currency || 'EUR'}
                        exchangeRates={exchangeRates}
                        onCheckout={startCheckout}
                    />
                )}
            </div>

            {isAddressModalOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl animate-in zoom-in-95 duration-200 max-h-[85vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-black text-gray-900">
                                {checkoutStep === 1 ? t('marketplace:checkout.address_title') : t('marketplace:checkout.shipping_method_label')}
                            </h3>
                            <button onClick={() => setIsAddressModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                <X size={20} className="text-gray-400" />
                            </button>
                        </div>

                        {checkoutStep === 1 ? (
                            <CheckoutAddressStep
                                addressData={addressData}
                                setAddressData={setAddressData}
                                errors={errors}
                                isAuthenticated={isAuthenticated}
                                email={email}
                                setEmail={setEmail}
                                isDifferentAddress={isDifferentAddress}
                                onDifferentAddressToggle={handleDifferentAddressToggle}
                                onSaveToProfile={handleSaveToProfile}
                                user={user}
                                openAuthModal={openAuthModal}
                            />
                        ) : (
                            (() => {
                                const getPODOrigin = (country: string) => {
                                    const EU_COUNTRIES = [
                                        'BELGIQUE', 'BELGIUM', 'FRANCE', 'ESPAGNE', 'SPAIN', 'ITALIE', 'ITALY',
                                        'ALLEMAGNE', 'GERMANY', 'PORTUGAL', 'PAYS-BAS', 'NETHERLANDS', 'AUTRICHE', 'AUSTRIA',
                                        'SUÈDE', 'SWEDEN', 'DANEMARK', 'DENMARK', 'FINLANDE', 'FINLAND', 'IRLANDE', 'IRELAND',
                                        'LUXEMBOURG', 'GRÈCE', 'GREECE', 'POLOGNE', 'POLAND', 'ROUMANIE', 'ROMANIA',
                                        'BULGARIE', 'BULGARIA', 'HONGRIE', 'HUNGARY', 'TCHÉQUIE', 'CZECHIA', 'SLOVAQUIE', 'SLOVAKIA',
                                        'SLOVÉNIE', 'SLOVENIA', 'CROATIE', 'CROATIA', 'ESTONIE', 'ESTONIA', 'LETTONIE', 'LATVIA',
                                        'LITUANIE', 'LITHUANIA', 'CHYPRE', 'CYPRUS', 'MALTE', 'MALTA'
                                    ];
                                    const NA_COUNTRIES = ['ETATS-UNIS', 'USA', 'UNITED STATES', 'CANADA'];

                                    const upper = country.trim().toUpperCase();
                                    if (EU_COUNTRIES.includes(upper)) return t('marketplace:checkout.shipping_region.europe');
                                    if (NA_COUNTRIES.includes(upper)) return t('marketplace:checkout.shipping_region.north_america');
                                    return t('marketplace:checkout.shipping_region.international');
                                };

                                const isAllPOD = items.every(i => i.shopId === 'PADEL_PARTNER_POD');
                                const podRegion = isAllPOD ? getPODOrigin(addressData.country) : '';

                                return (
                                    <CheckoutShippingStep
                                        availableRates={availableRates}
                                        selectedShippingId={selectedShippingId}
                                        setSelectedShippingId={setSelectedShippingId}
                                        totalAmount={totalAmount}
                                        commissionPct={commissionPct}
                                        minFee={defaultMinFee}
                                        currency={items[0]?.currency || 'EUR'}
                                        addressData={addressData}
                                        shippingOrigin={isAllPOD
                                            ? t('marketplace:checkout.shipping_origin_pod', { region: podRegion })
                                            : items[0]?.shopName ? t('marketplace:checkout.shipping_origin_merchant', { name: items[0].shopName, city: items[0].shopCity || '' }) : undefined}
                                    />
                                );
                            })()
                        )}

                        <div className="flex gap-4 mt-8">
                            <button
                                onClick={() => checkoutStep === 2 ? setCheckoutStep(1) : setIsAddressModalOpen(false)}
                                className="flex-1 bg-gray-100 text-gray-700 font-extrabold py-4 rounded-2xl transition-all"
                            >
                                {checkoutStep === 2 ? t('marketplace:cart.back') : t('marketplace:cart.cancel')}
                            </button>
                            <button
                                onClick={handleCheckout}
                                disabled={isProcessing || (checkoutStep === 2 && !selectedShippingId)}
                                className={`flex-[2] bg-purple-600 text-white font-extrabold py-4 rounded-2xl transition-all flex items-center justify-center gap-2 ${isProcessing ? 'opacity-70 cursor-not-allowed' : 'hover:bg-purple-700'}`}
                            >
                                {isProcessing ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        {t('common:loading')}
                                    </>
                                ) : (
                                    <>
                                        {checkoutStep === 1 ? t('marketplace:cart.next') : t('marketplace:cart.pay')}
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CartDrawer;
